import { CompanyQuery, useCreateOrUpdateCompanyMutation } from "@generated/graphql";
import { ApartmentOutlined } from "@mui/icons-material";
import { Divider, Grid, Stack } from "@mui/material";
import Card from "components/Card";
import FieldLabel from "components/FieldLabel";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import CheckboxControl from "components/form/CheckboxControl";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import { CompanyContext } from "context/companyContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import VALIDATION from "helpers/validation";
import { cleanSpaceInString, createValidation } from "helpers/validation/createValidation";
import { CompanyFormInputs } from "pages/Register/types";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

type CompanyInfoFormInputs = Pick<CompanyFormInputs, "fullName" | "inn" | "kpp" | "ogrn" | "registrationAddress"> & {
  factualAddress: string;
  isFactualAddressSame: boolean;
  documentEmail: string;
  phone: string;
};

const getFormValues = (company: CompanyQuery["company"]): CompanyInfoFormInputs => ({
  fullName: company?.fullName || "",
  inn: company?.inn || "",
  kpp: company?.kpp || "",
  ogrn: company?.ogrn || "",
  registrationAddress: company?.registrationAddress || "",
  factualAddress: company?.registrationAddress || "",
  isFactualAddressSame: true,
  documentEmail: "",
  phone: "",
});

const CompanyInfoForm = () => {
  const { addToast } = useContext(ToastContext);
  const { currentCompany, refetchCompany } = useContext(CompanyContext);
  const [isEditing, setIsEditing] = useState(false);
  const [updateCompany, { loading }] = useCreateOrUpdateCompanyMutation();
  const form = useForm<CompanyInfoFormInputs>({ defaultValues: getFormValues(currentCompany) });
  const { getValues, handleSubmit, reset, setValue, watch } = form;
  const isFactualAddressSame = watch("isFactualAddressSame");

  useEffect(() => {
    reset(getFormValues(currentCompany));
  }, [currentCompany, reset]);

  const handleCancel = () => {
    reset(getFormValues(currentCompany));
    setIsEditing(false);
  };

  const handleFactualAddressSameChange = (isSame: boolean) => {
    if (isSame) setValue("factualAddress", getValues("registrationAddress"));
  };

  const onSubmit = (formData: CompanyInfoFormInputs) => {
    if (!currentCompany) return;

    updateCompany({
      variables: {
        input: {
          id: currentCompany.id,
          managerPosition: currentCompany.managerPosition,
          managerFio: currentCompany.managerFio,
          shortName: currentCompany.shortName,
          fullName: cleanSpaceInString(formData.fullName),
          inn: cleanSpaceInString(formData.inn),
          type: currentCompany.type,
          userPosition: currentCompany.currentUserPosition || "",
          ogrnDate: currentCompany.ogrnDate,
          ogrn: cleanSpaceInString(formData.ogrn),
          kpp: cleanSpaceInString(formData.kpp),
          okato: currentCompany.okato,
          okpo: currentCompany.okpo,
          oktmo: currentCompany.oktmo,
          registrationDate: currentCompany.registrationDate,
          state: currentCompany.state,
          registrationAddress: cleanSpaceInString(formData.registrationAddress),
        },
      },
    })
      .then(() => refetchCompany())
      .then(() => {
        setIsEditing(false);
        addToast({ type: ToastTypeEnum.SUCCESS });
      })
      .catch((error) => parseApolloError(error, { addToast }));
  };

  return (
    <Card
      title='Реквизиты компании'
      startIcon={<ApartmentOutlined />}>
      <Form form={form}>
        <Stack
          spacing={2.5}
          useFlexGap>
          <TextFieldControl
            label='Название компании'
            name='fullName'
            disabled={!isEditing}
            variant='outlined'
            rules={{ required: true, ...VALIDATION.companyName }}
          />
          <Grid
            container
            spacing={2.5}>
            <Grid
              item
              xs={12}
              md={4}>
              <TextFieldControl
                label='ИНН'
                name='inn'
                disabled={!isEditing}
                variant='outlined'
                hint='10 цифр для юрлица или 12 для ИП'
                rules={{ required: true, ...VALIDATION.inn }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}>
              <TextFieldControl
                label='КПП'
                name='kpp'
                disabled={!isEditing}
                variant='outlined'
                hint='9 цифр, только для юрлиц'
                rules={{
                  ...createValidation({
                    pattern: { value: /^[0-9]{9}$/, message: "Должно быть 9 цифр" },
                  }),
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}>
              <TextFieldControl
                label='ОГРН'
                name='ogrn'
                disabled={!isEditing}
                variant='outlined'
                hint='13 цифр для юрлица или 15 для ИП'
                rules={{
                  required: true,
                  ...createValidation({
                    pattern: { value: /^(?:[0-9]{13}|[0-9]{15})$/, message: "Должно быть 13 или 15 цифр" },
                  }),
                }}
              />
            </Grid>
          </Grid>
          <Divider />
          <TextFieldControl
            label='Юридический адрес'
            name='registrationAddress'
            disabled={!isEditing}
            multiline
            minRows={3}
            variant='outlined'
            hint='Индекс, город, улица, дом, офис'
            rules={{ required: true, ...VALIDATION.address }}
          />
          <Stack
            spacing={1}
            useFlexGap>
            <FieldLabel label='Фактический адрес' />
            <CheckboxControl
              name='isFactualAddressSame'
              label='Фактический адрес совпадает с юридическим'
              disabled={!isEditing}
              handleChange={handleFactualAddressSameChange}
              sx={{ ml: 0 }}
            />
            {!isFactualAddressSame && (
              <TextFieldControl
                label='Фактический адрес'
                name='factualAddress'
                disabled={!isEditing}
                multiline
                minRows={3}
                variant='outlined'
                rules={VALIDATION.address}
              />
            )}
          </Stack>
          <Divider />
          <Grid
            container
            spacing={2.5}>
            <Grid
              item
              xs={12}
              md={6}>
              <TextFieldControl
                label='Email для документов'
                name='documentEmail'
                type='email'
                disabled={!isEditing}
                variant='outlined'
                hint='Счета и акты придут на этот адрес'
                rules={VALIDATION.email}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}>
              <TextFieldControl
                label='Телефон'
                name='phone'
                type='tel'
                disabled={!isEditing}
                variant='outlined'
                hint='Отображается в счете'
              />
            </Grid>
          </Grid>
          <Stack sx={{ width: 256 }}>
            <SaveEditButtons
              isEdited={isEditing}
              onSubmit={handleSubmit(onSubmit)}
              setIsEdited={setIsEditing}
              loading={loading}
              cancelButtonProps={{ onClick: handleCancel }}
            />
          </Stack>
        </Stack>
      </Form>
    </Card>
  );
};

export default CompanyInfoForm;
